import {
  AccountTransactionPaginatedRecord,
  BondsmithPageAccountTransactionPaginatedRecord,
  ProductAccountTransactionResponse,
} from '@b7hio/api-lib/src/ops-portal';
import { superstructResolver, FormFriendly } from '@b7hio/core-lib/src/form';
import { useTranslation } from 'react-i18next';
import { NextSeo } from 'next-seo';
import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { create } from 'superstruct';
import { useDebouncedCallback } from 'use-debounce';
import { PageHeader } from '../../components';
import { TransactionList } from './components';
import type { GridSortModel } from '@mui/x-data-grid-pro';

import {
  defaultValues,
  transactionsListFilterValidation,
  TransactionListFilterValues,
} from './components/TransactionListFilter/TransactionListFilter.validation';

interface Props {
  // List State
  readonly isLoading: boolean;
  readonly data?: BondsmithPageAccountTransactionPaginatedRecord;

  // View State
  readonly state: 'list';

  readonly initialFilter?: TransactionListFilterValues;

  // Events
  readonly onFilterChange: (value: TransactionListFilterValues) => void;
  readonly pageNumber: number;
  readonly setPageNumber: any;
  readonly sortModel?: GridSortModel;
  readonly setSortModel: any;
  readonly resultsPerPage: number;
}

const initialFilter = defaultValues();

export const TransactionListPage = ({
  isLoading,
  data,
  onFilterChange,
  pageNumber,
  setPageNumber,
  sortModel,
  setSortModel,
  resultsPerPage,
}: Props): JSX.Element => {
  const { t } = useTranslation(['common', 'transactions']);

  const methods = useForm<FormFriendly<TransactionListFilterValues>>({
    mode: 'onChange',
    resolver: superstructResolver(transactionsListFilterValidation, {
      coerce: true,
    }),
    defaultValues: initialFilter ?? defaultValues(),
  });

  const submitForm = useDebouncedCallback(
    async (values: FormFriendly<TransactionListFilterValues>) => {
      await onFilterChange(create(values, transactionsListFilterValidation));
    },
    200
  );

  // Form Listener
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const subscription = methods.watch(submitForm as any);
    return () => subscription.unsubscribe();
  }, [methods.watch, onFilterChange, submitForm]);

  return (
    <>
      <NextSeo title={t('transactions:page.title')} />
      <PageHeader title={t('transactions:page.title')} />
      <FormProvider {...methods}>
        <TransactionList
          isLoading={isLoading}
          data={data}
          pageNumber={pageNumber}
          setPageNumber={setPageNumber}
          filtersNotRequired
          sortModel={sortModel}
          setSortModel={setSortModel}
          resultsPerPage={resultsPerPage}
        />
      </FormProvider>
    </>
  );
};
